import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const UserApproval = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No token found, please log in.');
        navigate('/');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pending-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 403) {
        alert('Access denied. Owner role required.');
        navigate('/');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch pending users');
      }

      const data = await response.json();
      setPendingUsers(data.users);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No token found, please log in.');
        navigate('/');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/all-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 403) {
        alert('Access denied. Owner role required.');
        navigate('/');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch all users');
      }

      const data = await response.json();
      setAllUsers(data.users);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/update-user-approval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userId, action })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} user`);
      }

      const data = await response.json();
      setMessage(`Success: ${data.message}`);
      
      // Refresh the lists
      fetchPendingUsers();
      fetchAllUsers();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (email, username) => {
    if (!window.confirm(`Resend verification email to ${email}?`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email, username })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to resend verification');
      }

      const data = await response.json();
      setMessage(`Success: ${data.message}`);
      
      // Refresh the lists
      fetchPendingUsers();
      fetchAllUsers();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (email, username) => {
    if (!window.confirm(`Are you sure you want to remove user "${username}" (${email})? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/remove-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email, username })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to remove user');
      }

      const data = await response.json();
      setMessage(`Success: ${data.message}`);
      
      // Refresh the lists
      fetchPendingUsers();
      fetchAllUsers();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
    fetchAllUsers();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (user) => {
    if (!user.is_verified) {
      return <span className="badge badge-warning">Email Not Verified</span>;
    } else if (!user.is_approved) {
      return <span className="badge badge-pending">Pending Approval</span>;
    } else {
      return <span className="badge badge-approved">Approved</span>;
    }
  };

  return (
    <div className="user-approval-container">
      <style jsx>{`
        .user-approval-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: Arial, sans-serif;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
          color: #333;
        }
        
        .tabs {
          display: flex;
          margin-bottom: 20px;
          border-bottom: 2px solid #eee;
        }
        
        .tab {
          padding: 10px 20px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          border-bottom: 2px solid transparent;
          transition: all 0.3s ease;
        }
        
        .tab.active {
          border-bottom-color: #007bff;
          color: #007bff;
          font-weight: bold;
        }
        
        .tab:hover {
          background-color: #f8f9fa;
        }
        
        .message {
          padding: 10px;
          margin-bottom: 20px;
          border-radius: 4px;
          background-color: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }
        
        .users-table {
          width: 100%;
          border-collapse: collapse;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          border-radius: 8px;
          overflow: hidden;
        }
        
        .users-table th,
        .users-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #eee;
        }
        
        .users-table th {
          background-color: #f8f9fa;
          font-weight: bold;
          color: #333;
        }
        
        .users-table tr:hover {
          background-color: #f8f9fa;
        }
        
        .badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
        }
        
        .badge-warning {
          background-color: #fff3cd;
          color: #856404;
        }
        
        .badge-pending {
          background-color: #cce5ff;
          color: #0066cc;
        }
        
        .badge-approved {
          background-color: #d4edda;
          color: #155724;
        }
        
        .action-buttons {
          display: flex;
          gap: 10px;
        }
        
        .btn {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }
        
        .btn-approve {
          background-color: #28a745;
          color: white;
        }
        
        .btn-approve:hover {
          background-color: #218838;
        }
        
        .btn-reject {
          background-color: #dc3545;
          color: white;
        }
        
        .btn-reject:hover {
          background-color: #c82333;
        }
        
        .btn-resend {
          background-color: #007bff;
          color: white;
        }
        
        .btn-resend:hover {
          background-color: #0056b3;
        }
        
        .btn-remove {
          background-color: #6c757d;
          color: white;
        }
        
        .btn-remove:hover {
          background-color: #545b62;
        }
        
        .btn-back {
          background-color: #6c757d;
          color: white;
          margin-bottom: 20px;
        }
        
        .btn-back:hover {
          background-color: #545b62;
        }
        
        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }
        
        .empty-state {
          text-align: center;
          padding: 40px;
          color: #666;
          font-style: italic;
        }
      `}</style>

      <button className="btn btn-back" onClick={() => navigate('/landing')}>
        ← Back to Landing Page
      </button>

      <div className="header">
        <h1>User Management Dashboard</h1>
        <p>Approve or reject user registrations</p>
      </div>

      {message && (
        <div className="message">
          {message}
        </div>
      )}

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending Approvals ({pendingUsers.length})
        </button>
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Users ({allUsers.length})
        </button>
      </div>

      {loading && (
        <div className="loading">Loading...</div>
      )}

      {!loading && activeTab === 'pending' && (
        <div>
          <h2>Users Pending Approval</h2>
          {pendingUsers.length === 0 ? (
            <div className="empty-state">
              No users pending approval
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Registration Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{user.role}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{getStatusBadge(user)}</td>
                    <td>
                      <div className="action-buttons">
                        {user.is_verified ? (
                          <>
                            <button 
                              className="btn btn-approve"
                              onClick={() => handleUserAction(user.id, 'approve')}
                              disabled={loading}
                            >
                              Approve
                            </button>
                            <button 
                              className="btn btn-reject"
                              onClick={() => handleUserAction(user.id, 'reject')}
                              disabled={loading}
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <button 
                            className="btn btn-resend"
                            onClick={() => handleResendVerification(user.email, user.username)}
                            disabled={loading}
                          >
                            Resend Verification
                          </button>
                        )}
                        <button 
                          className="btn btn-remove"
                          onClick={() => handleRemoveUser(user.email, user.username)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && activeTab === 'all' && (
        <div>
          <h2>All Users</h2>
          {allUsers.length === 0 ? (
            <div className="empty-state">
              No users found
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Registration Date</th>
                  <th>Status</th>
                  <th>Approved By</th>
                  <th>Approved Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{user.role}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{getStatusBadge(user)}</td>
                    <td>{user.approved_by || '-'}</td>
                    <td>{user.approved_at ? formatDate(user.approved_at) : '-'}</td>
                    <td>
                      <div className="action-buttons">
                        {!user.is_verified && (
                          <button 
                            className="btn btn-resend"
                            onClick={() => handleResendVerification(user.email, user.username)}
                            disabled={loading}
                            title="Resend verification email"
                          >
                            Resend
                          </button>
                        )}
                        {user.role !== 'owner' && (
                          <button 
                            className="btn btn-remove"
                            onClick={() => handleRemoveUser(user.email, user.username)}
                            disabled={loading}
                            title="Remove user"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default UserApproval;
